import { useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";

import { useGraphqlClient } from "@/lib/graphqlClient";
import { LIVE_QUERY_STALE_TIME_MS } from "@/hooks/dashboard/useAutoRefresh";

const CAMPAIGN_EVENTS_QUERY = gql`
  query MultiChartCampaignEvents($tenant_ids: [Int!]) {
    campaign_schedule(tenant_ids: $tenant_ids) {
      event_id
      event_label
      event_start
      event_end
      campaign_start
      campaign_end
      event_type_label
      collection_group
      tenant_id
      tenant_label
      protected_event_id
      channel
    }
  }
`;

interface CampaignEventRow {
  event_id: string;
  event_label: string;
  event_start: string;
  event_end: string;
  campaign_start: string;
  campaign_end: string;
  event_type_label: string;
  collection_group: string | null;
  tenant_id: string | number;
  tenant_label: string;
  protected_event_id: string | number;
  channel: string;
}

type CampaignEventsResponse = { campaign_schedule: CampaignEventRow[] };

export interface MultiChartEvent {
  event_id: string;
  event_label: string;
  event_start: string;
  event_end: string;
  event_type_label: string;
  collection_group: string;
  tenants?: { id: string; label: string }[];
  protectedEvents?: { id: string; channel: string; tenantId: string }[];
}

function parseTime(value: string | null | undefined): number {
  if (!value) return Number.NaN;
  let iso = value.includes("T") ? value : value.replace(" ", "T");
  if (!/([zZ]|[+-]\d{2}:?\d{2})$/.test(iso)) iso += "Z";
  return new Date(iso).getTime();
}

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

/**
 * Distinct events that are live or past and whose start is within the last
 * 3 days, deduplicated from the engine-grain campaign schedule and ordered
 * newest first. Filtering is done client-side.
 */
export function useMultiChartEvents(
  referenceTime: number,
  tenantIds: string[],
  options: {
    enabled?: boolean;
    includeAll?: boolean;
    activeCampaignsOnly?: boolean;
    includeActiveCampaigns?: boolean;
    refetchOnWindowFocus?: boolean;
    refetchOnReconnect?: boolean;
  } = {},
) {
  const client = useGraphqlClient();
  const numericTenantIds = tenantIds.map(Number);

  return useQuery({
    queryKey: [
      "multi-chart-events",
      referenceTime,
      numericTenantIds,
      options.activeCampaignsOnly,
      options.includeActiveCampaigns,
      options.includeAll,
    ],
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
    refetchOnReconnect: options.refetchOnReconnect,
    queryFn: async () => {
      const data = await client.request<CampaignEventsResponse>(CAMPAIGN_EVENTS_QUERY, {
        tenant_ids: numericTenantIds.length > 0 ? numericTenantIds : undefined,
      });
      const byId = new Map<string, MultiChartEvent>();

      for (const row of data.campaign_schedule ?? []) {
        const startMs = parseTime(row.event_start);
        const campaignStart = parseTime(row.campaign_start);
        const campaignEnd = parseTime(row.campaign_end);
        const activeCampaign = campaignStart <= referenceTime && referenceTime <= campaignEnd;
        const recentEvent = startMs <= referenceTime && startMs >= referenceTime - THREE_DAYS_MS;
        if (
          !options.includeAll &&
          (options.activeCampaignsOnly
            ? !activeCampaign
            : !recentEvent && !(options.includeActiveCampaigns && activeCampaign))
        )
          continue;
        // GraphQL returns numeric IDs; the app (REST tenants, URLs) compares strings.
        const tenantId = row.tenant_id == null ? "" : String(row.tenant_id);
        const protectedEventId =
          row.protected_event_id == null ? "" : String(row.protected_event_id);
        const existing = byId.get(row.event_id);
        if (existing) {
          if (tenantId && !existing.tenants?.some((tenant) => tenant.id === tenantId))
            existing.tenants?.push({ id: tenantId, label: row.tenant_label });
          if (
            protectedEventId &&
            !existing.protectedEvents?.some((item) => item.id === protectedEventId)
          )
            existing.protectedEvents?.push({
              id: protectedEventId,
              channel: row.channel,
              tenantId,
            });
          continue;
        }
        if (!Number.isFinite(startMs)) continue;

        byId.set(row.event_id, {
          event_id: row.event_id,
          event_label: row.event_label,
          event_start: row.event_start,
          event_end: row.event_end,
          event_type_label: row.event_type_label,
          collection_group: row.collection_group ?? "",
          tenants: tenantId ? [{ id: tenantId, label: row.tenant_label }] : [],
          protectedEvents: protectedEventId
            ? [{ id: protectedEventId, channel: row.channel, tenantId }]
            : [],
        });
      }

      return [...byId.values()].sort((a, b) => parseTime(b.event_start) - parseTime(a.event_start));
    },
    placeholderData: (previousData) => previousData,
    staleTime: LIVE_QUERY_STALE_TIME_MS,
  });
}
